import { useRef } from 'react';

interface LongPressOptions {
  delay?: number;
  slop?: number;
}

/**
 * 터치 롱프레스 제스처 훅 (ChatWindow 메시지 롱프레스 패턴의 훅화).
 *
 * - delay(기본 500ms) 홀드 시 onLongPress(터치 좌표) 호출 + 짧은 진동
 * - slop(기본 10px) 이내 미세 떨림은 유지, 그 이상 이동(스크롤)은 취소
 * - 발화 후 touchend에서 preventDefault로 compat mouse 이벤트 억제 (1차 방어),
 *   억제가 안 되는 브라우저의 ghost click은 onClickCapture 가드가 삼킨다 (2차 방어)
 * - Android 네이티브 contextmenu는 터치 제스처 중일 때만 억제 (데스크톱 우클릭은 유지)
 *
 * 반환된 핸들러 객체를 대상 요소에 스프레드해서 쓴다:
 *   <div {...longPress.handlers} onClick={...}>
 */
export function useLongPress(
  onLongPress: (pos: { x: number; y: number }) => void,
  { delay = 500, slop = 10 }: LongPressOptions = {}
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);
  const firedAtRef = useRef(0);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handlers = {
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0];
      startPosRef.current = { x: t.clientX, y: t.clientY };
      firedRef.current = false;
      clearTimer();
      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        firedAtRef.current = Date.now();
        navigator.vibrate?.(10);
        const start = startPosRef.current;
        if (start) onLongPress(start);
      }, delay);
    },
    onTouchMove: (e: React.TouchEvent) => {
      if (firedRef.current) return; // 발화 후 이동은 무시 (열린 메뉴 유지)
      const start = startPosRef.current;
      if (!start) return;
      const t = e.touches[0];
      if (Math.abs(t.clientX - start.x) > slop || Math.abs(t.clientY - start.y) > slop) {
        clearTimer();
      }
    },
    onTouchEnd: (e: React.TouchEvent) => {
      clearTimer();
      if (firedRef.current) {
        firedRef.current = false;
        e.preventDefault();
      }
      startPosRef.current = null;
    },
    onTouchCancel: () => {
      clearTimer();
      firedRef.current = false;
      startPosRef.current = null;
    },
    onContextMenu: (e: React.MouseEvent) => {
      if (startPosRef.current) e.preventDefault();
    },
    onClickCapture: (e: React.MouseEvent) => {
      // 롱프레스 발화 직후의 ghost click이 행 onClick(채팅방 진입)으로 새는 것 차단
      if (Date.now() - firedAtRef.current < 600) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
  };

  return { handlers };
}
